import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge, { formatCurrency } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ScopeLinesSection from "@/components/ScopeLinesSection";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { RotateCcw, GripVertical } from "lucide-react";

export default function ProjectDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [orderedStages, setOrderedStages] = useState(null);   // local override while reordering
  const navigate = useNavigate();
  const { user } = useAuth();

  const reload = () => api.get(`/api/projects/${id}`).then((r) => { setData(r.data); setOrderedStages(null); });
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const canReorder = hasRole(user, "admin", "business_development");
  const stages = orderedStages || data?.payment_stages || [];

  const handleDragStart = (i) => setDragIndex(i);
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = async (targetIdx) => {
    if (dragIndex === null || dragIndex === targetIdx) { setDragIndex(null); return; }
    const list = [...stages];
    const [moved] = list.splice(dragIndex, 1);
    list.splice(targetIdx, 0, moved);
    setOrderedStages(list);
    setDragIndex(null);
    // Persist
    try {
      await api.post(`/api/projects/${id}/payment-stages/reorder`, { stage_ids: list.map((s) => s.id) });
      toast.success("Payment schedule reordered");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reorder failed");
      reload();
    }
  };

  const reopen = async () => {
    const reason = window.prompt("Reopen this project? Reason (optional):");
    if (reason === null) return;
    try {
      await api.post(`/api/projects/${id}/reopen`, { reason: reason || null });
      toast.success("Project reopened");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const { project, client, invoices, activity, financials } = data;

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      <div>
        <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900">← Back</button>
        <div className="flex items-center gap-3 mt-1">
          <h1 className="text-3xl font-heading font-semibold tracking-tight">{project.project_name}</h1>
          <StatusBadge status={project.status} />
          <StatusBadge status={project.payment_status} />
          {project.is_closed && <span className="text-[10px] uppercase tracking-wide bg-slate-900 text-white rounded-full px-2 py-0.5">closed</span>}
          {project.is_closed && hasRole(user, "admin", "business_development") && (
            <Button size="sm" variant="outline" onClick={reopen} data-testid="reopen-project-btn">
              <RotateCcw className="w-3 h-3 mr-1" /> Reopen as Change Order
            </Button>
          )}
        </div>
        <div className="text-sm text-slate-500 mt-1">
          <span className="font-mono">{project.project_code}</span> · {client?.firm_name} · {project.currency}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-none p-5">
          <div className="text-xs uppercase text-slate-500">Current Project Value</div>
          <div className="font-heading text-xl font-semibold mt-1">
            {formatCurrency(project.current_project_value ?? financials.project_amount, project.currency)}
          </div>
          {project.current_project_value != null && Math.abs((project.current_project_value || 0) - (project.original_project_amount ?? project.project_amount ?? 0)) > 0.01 && (
            <div className="text-[11px] text-slate-500 mt-1 leading-tight">
              Original {formatCurrency(project.original_project_amount ?? project.project_amount, project.currency)}
              {(project.approved_additional_work || 0) > 0 && <> · <span className="text-emerald-700">+{formatCurrency(project.approved_additional_work, project.currency)} additions</span></>}
              {(project.approved_deductions || 0) > 0 && <> · <span className="text-rose-700">-{formatCurrency(project.approved_deductions, project.currency)} deductions</span></>}
            </div>
          )}
        </Card>
        <Card className="border-slate-200 shadow-none p-5">
          <div className="text-xs uppercase text-slate-500">Invoiced</div>
          <div className="font-heading text-xl font-semibold mt-1">{formatCurrency(financials.total_invoiced, project.currency)}</div>
        </Card>
        <Card className="border-slate-200 shadow-none p-5">
          <div className="text-xs uppercase text-slate-500">Received</div>
          <div className="font-heading text-xl font-semibold mt-1 text-emerald-700">{formatCurrency(financials.total_received, project.currency)}</div>
        </Card>
        <Card className="border-slate-200 shadow-none p-5">
          <div className="text-xs uppercase text-slate-500">Outstanding</div>
          {(() => {
            const outstanding = Math.max(0, Number(financials.total_outstanding || project.total_outstanding || 0));
            const overpaid = Number(project.overpaid_amount || 0);
            const isSettled = outstanding <= 0.01 && overpaid <= 0.01 && Number(project.total_received || 0) > 0;
            return (
              <>
                <div className={`font-heading text-xl font-semibold mt-1 ${isSettled ? "text-emerald-700" : "text-amber-700"}`}>
                  {isSettled ? "Settled" : formatCurrency(outstanding, project.currency)}
                </div>
                {overpaid > 0.01 && (
                  <div className="text-[11px] text-rose-700 mt-1" title="Amount received exceeds current project value. Reconcile with the payer or add a deduction/CO to explain the extra receipt.">
                    ⚠ Over-paid by {formatCurrency(overpaid, project.currency)}
                  </div>
                )}
                {!isSettled && overpaid <= 0.01 && (
                  <div className="text-xs text-slate-500 mt-1">{financials.percent_collected}% collected</div>
                )}
              </>
            );
          })()}
        </Card>
      </div>

      <Tabs defaultValue="scope">
        <TabsList>
          <TabsTrigger value="scope" data-testid="tab-scope">Scope / Change Orders</TabsTrigger>
          <TabsTrigger value="stages">Payment Schedule</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="activity">Activity Log</TabsTrigger>
        </TabsList>

        <TabsContent value="scope">
          <ScopeLinesSection project={project} onChanged={reload} />
        </TabsContent>

        <TabsContent value="stages">
          <Card className="border-slate-200 shadow-none p-4 mt-4">
            {canReorder && stages.length > 1 && (
              <div className="text-[11px] text-slate-500 mb-2">
                Drag the <GripVertical className="w-3 h-3 inline align-text-bottom" /> handle to reorder — the sequence controls the order invoices will be raised in.
              </div>
            )}
            <table className="data-table w-full">
              <thead><tr>
                {canReorder && <th className="w-6"></th>}
                <th>#</th><th>Stage</th><th>%</th><th>Amount</th><th className="text-orange-700">Invoice on</th><th>Invoice #</th><th>Received</th><th>Status</th>
              </tr></thead>
              <tbody>
                {stages.map((s, idx) => (
                  <tr key={s.id}
                      draggable={canReorder}
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(idx)}
                      className={dragIndex === idx ? "opacity-50" : ""}
                      data-testid={`stage-row-${s.id}`}>
                    {canReorder && (
                      <td className="cursor-grab text-slate-400" title="Drag to reorder">
                        <GripVertical className="w-4 h-4" />
                      </td>
                    )}
                    <td className="font-mono text-xs">{idx + 1}</td>
                    <td className="font-medium">
                      {s.stage_name}
                      {s.scope_line_ref && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-sky-50 border border-sky-200 text-sky-800 text-[10px] px-1.5 py-0.5" title="From this scope line">
                          {s.scope_line_ref}
                        </span>
                      )}
                    </td>
                    <td>{s.percentage}%</td>
                    <td>{formatCurrency(s.expected_amount, s.currency)}</td>
                    <td className="text-orange-700">{s.expected_invoice_date?.slice(0, 10) || "—"}</td>
                    <td className="font-mono text-xs">{s.invoice_number || "—"}</td>
                    <td className="text-emerald-700">{formatCurrency(s.payment_received_amount || 0, s.currency)}</td>
                    <td><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="invoices">
          <Card className="border-slate-200 shadow-none p-4 mt-4">
            <table className="data-table w-full">
              <thead><tr><th>Invoice</th><th>Date</th><th>Due</th><th>Amount</th><th>Received</th><th>Outstanding</th><th>Status</th></tr></thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <div className="font-mono text-xs">{i.invoice_number}</div>
                      {i.is_consolidated_for_project && (
                        <div className="text-[10px] mt-0.5 inline-flex items-center rounded-full bg-brand-tint text-brand-dark border border-brand/30 px-1.5 py-0.5" title="Invoice consolidated across multiple projects. Values shown are this project's prorated share.">
                          Consolidated · {i.project_count_on_invoice} projects
                        </div>
                      )}
                    </td>
                    <td>{i.invoice_date?.slice(0, 10)}</td>
                    <td>{i.due_date?.slice(0, 10)}</td>
                    <td>{formatCurrency(i.total_invoice_amount_for_project ?? i.total_invoice_amount, i.currency)}</td>
                    <td className="text-emerald-700">{formatCurrency(i.amount_received_for_project ?? i.amount_received, i.currency)}</td>
                    <td className="text-amber-700">{formatCurrency(i.outstanding_amount_for_project ?? i.outstanding_amount, i.currency)}</td>
                    <td><StatusBadge status={i.payment_status} /></td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-6">No invoices yet.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <Card className="border-slate-200 shadow-none p-6 mt-4 space-y-3">
            {[
              ["Project Created", project.created_at],
              ["Project Start", project.project_start_date],
              ["Expected Submission", project.expected_submission_date],
              ["Actual Submission", project.actual_submission_date],
              ["Client Approval", project.client_approval_date],
            ].map(([label, val]) => (
              <div key={label} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                <span className="text-slate-500">{label}</span>
                <span className="font-medium">{val ? val.slice(0, 10) : "—"}</span>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="activity">
          <Card className="border-slate-200 shadow-none p-4 mt-4">
            <div className="space-y-2">
              {activity.map((a) => (
                <div key={a.id} className="flex items-start gap-3 text-sm border-b border-slate-100 pb-2">
                  <div className="w-2 h-2 rounded-full bg-slate-400 mt-2"></div>
                  <div className="flex-1">
                    <div><span className="font-medium">{a.user_email}</span> <span className="text-slate-500">{a.action}</span> <span className="text-slate-400">{a.entity_type}</span></div>
                    <div className="text-xs text-slate-400">{a.created_at?.replace("T", " ").slice(0, 19)}</div>
                  </div>
                </div>
              ))}
              {activity.length === 0 && <div className="text-slate-500 text-sm py-4">No activity recorded yet.</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
